import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { CheckCircle, Loader2, Clock, MapPin, Phone, FileText, Download } from "lucide-react";
import { useCart, API } from "@/App";

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderNumber = searchParams.get("order");
  const paymentId = searchParams.get("paymentId");
  const { clearCart } = useCart();
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const receiptRef = useRef(null);

  // Business Information for Legal Receipt
  const businessInfo = {
    name: "Masala Indisk Restaurant og Bar",
    legalName: "Vedant AS",
    orgNumber: "930685038",
    address: "Rikard Kaarbøs plass 1",
    postCode: "9405 Harstad",
    country: "Norge",
    phone: "+47 48 91 51 90",
    email: "post@masalaindisk.no",
  };

  // Download PDF receipt
  const downloadReceipt = () => {
    if (!orderDetails) return;
    const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderDetails.order_number}/receipt`;
    window.open(pdfUrl, '_blank');
  };

  // Print receipt directly (opens PDF in new tab for printing)
  const printReceipt = () => {
    if (!orderDetails) return;
    const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderDetails.order_number}/receipt`;
    const printWindow = window.open(pdfUrl, '_blank');
    if (printWindow) {
      printWindow.addEventListener('load', () => {
        printWindow.print();
      });
    }
  };

  useEffect(() => {
    const verifyPayment = async () => {
      if (orderNumber) {
        try {
          // Verify payment status with backend
          const response = await axios.post(`${API}/payment/verify/${orderNumber}`);
          if (response.data.status === 'paid') {
            setVerified(true);
            clearCart();
          }
          
          // Fetch order details for pickup time
          try {
            const orderResponse = await axios.get(`${API}/orders/${orderNumber}`);
            setOrderDetails(orderResponse.data);
          } catch (e) {
          }
        } catch (error) {
          console.error("Payment verification error:", error);
          // Even if verification fails, show success since user was redirected here
          setVerified(true);
          clearCart();
        }
      } else {
        setVerified(true);
        clearCart();
      }
      setVerifying(false);
    };

    verifyPayment();
  }, [orderNumber]);

  if (verifying) {
    return (
      <div className="min-h-screen" style={{ background: '#f4e7d8' }}>
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4" style={{ color: '#721111' }} />
            <p className="text-lg" style={{ color: '#4a3728' }}>Bekrefter betaling...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#f4e7d8' }}>
      <Navbar />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-xl shadow-lg p-12 text-center"
          style={{ border: '3px solid #d4af37' }}
          data-testid="payment-success-container"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="inline-flex items-center justify-center w-24 h-24 rounded-full mb-6"
            style={{ background: '#22c55e' }}
          >
            <CheckCircle className="h-12 w-12 text-white" />
          </motion.div>

          <h1 
            className="text-4xl font-serif-display font-bold mb-4"
            style={{ color: '#2d1810' }}
            data-testid="success-title"
          >
            Betaling Vellykket!
          </h1>
          <p className="text-lg mb-8" style={{ color: '#4a3728' }}>
            Takk for din bestilling. Betalingen er behandlet.
          </p>

          {orderNumber && (
            <div 
              className="rounded-xl p-6 mb-8"
              style={{ background: '#f4e7d8', border: '2px solid #d4af37' }}
            >
              <p className="text-sm mb-2" style={{ color: '#8b4513' }}>Ordrenummer</p>
              <p 
                className="text-3xl font-bold"
                style={{ color: '#721111' }}
                data-testid="order-number"
              >
                {orderNumber}
              </p>
            </div>
          )}

          {/* Estimated Pickup Time */}
          <div 
            className="rounded-xl p-6 mb-8"
            style={{ background: '#dcfce7', border: '2px solid #22c55e' }}
            data-testid="pickup-time-section"
          >
            <div className="flex items-center justify-center gap-2 mb-3">
              <Clock className="h-6 w-6" style={{ color: '#166534' }} />
              <p className="font-semibold text-lg" style={{ color: '#166534' }}>Estimert hentetid</p>
            </div>
            {orderDetails?.estimated_minutes ? (
              <p className="text-3xl font-bold" style={{ color: '#15803d' }} data-testid="pickup-minutes">
                ca. {orderDetails.estimated_minutes} minutter
              </p>
            ) : (
              <p className="text-2xl font-bold" style={{ color: '#15803d' }} data-testid="pickup-default">
                ca. 20-30 minutter
              </p>
            )}
            <p className="text-sm mt-2" style={{ color: '#166534' }}>
              Du mottar en e-post når maten er klar!
            </p>
          </div>

          {/* Pickup Location */}
          <div className="p-4 rounded-xl mb-8" style={{ background: '#ede1c8' }}>
            <div className="flex items-center justify-center gap-2 mb-2">
              <MapPin className="h-5 w-5" style={{ color: '#721111' }} />
              <p className="font-semibold" style={{ color: '#2d1810' }}>Hent bestillingen her:</p>
            </div>
            <p className="text-base" style={{ color: '#4a3728' }}>
              Rikard Kaarbøs plass 1, 9405 Harstad
            </p>
            <div className="flex items-center justify-center gap-2 mt-3">
              <Phone className="h-4 w-4" style={{ color: '#721111' }} />
              <a href="tel:+4748915190" className="hover:underline" style={{ color: '#721111' }}>
                +47 48 91 51 90
              </a>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {orderNumber && (
              <Button
                onClick={() => navigate(`/track/${orderNumber}`)}
                className="rounded-full btn-maroon px-8 py-6 font-bold transition-transform hover:scale-105"
                data-testid="track-order-button"
              >
                Spor Din Bestilling
              </Button>
            )}
            <Button
              onClick={() => navigate("/")}
              className="rounded-full px-8 py-6"
              style={{ background: 'transparent', border: '2px solid #721111', color: '#721111' }}
              data-testid="back-home-button"
            >
              Tilbake til Forsiden
            </Button>
          </div>

          {/* Receipt Section */}
          {orderDetails && (
            <div 
              className="mt-8 p-6 rounded-xl"
              style={{ background: '#f8f4ef', border: '1px solid #d4af37' }}
            >
              <div className="flex items-center justify-center gap-2 mb-4">
                <FileText className="h-5 w-5" style={{ color: '#721111' }} />
                <p className="font-semibold" style={{ color: '#2d1810' }}>Trenger du kvittering?</p>
              </div>
              <p className="text-sm mb-4" style={{ color: '#4a3728' }}>
                Last ned eller skriv ut en offisiell kvittering med alle bedriftsdetaljer for regnskapsformål.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={downloadReceipt}
                  className="rounded-full px-6 py-3"
                  style={{ background: '#166534', color: 'white' }}
                  data-testid="download-receipt-button"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Last ned Kvittering
                </Button>
                <Button
                  onClick={printReceipt}
                  className="rounded-full px-6 py-3"
                  style={{ background: 'transparent', border: '2px solid #166534', color: '#166534' }}
                  data-testid="print-receipt-button"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Skriv ut Kvittering
                </Button>
              </div>
              <p className="text-xs mt-4" style={{ color: '#8b4513' }}>
                Kvitteringen inneholder: {businessInfo.name} | {businessInfo.legalName} | Org.nr: {businessInfo.orgNumber}
              </p>
            </div>
          )}
        </motion.div>
      </div>

      <Footer />
    </div>
  );
};

export default PaymentSuccessPage;
